import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import FormattedInput from './FormattedInput';
import { cepService, type EnderecoViaCep } from '../services/cep.service';

interface CepInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressFound?: (endereco: EnderecoViaCep) => void;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  id?: string;
  name?: string;
}

export default function CepInput({
  value,
  onChange,
  onAddressFound,
  isLoading = false,
  required = false,
  disabled = false,
  className = '',
  placeholder = '00000-000',
  id,
  name,
}: CepInputProps) {
  const [loading, setLoading] = useState(false);

  const handleSearch = async (cep: string) => {
    const clean = cep.replace(/\D/g, '');
    if (!clean || clean.length !== 8) return;

    try {
      setLoading(true);
      const endereco = await cepService.buscarCep(clean);
      if (endereco && onAddressFound) {
        onAddressFound(endereco);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (val: string) => {
    onChange(val);
    const clean = val.replace(/\D/g, '');
    if (clean.length === 8) {
      handleSearch(clean);
    }
  };

  return (
    <div className="relative">
      <FormattedInput
        type="cep"
        value={value}
        onChange={handleChange}
        className={`w-full pr-10 ${className}`}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        id={id}
        name={name}
      />
      <button
        type="button"
        onClick={() => handleSearch(value)}
        disabled={loading || isLoading || disabled}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 disabled:opacity-40 transition-colors p-1"
        title="Buscar CEP"
      >
        {loading || isLoading ? (
          <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
        ) : (
          <Search className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

export { CepInput };
